import * as THREE from "three";
import { RoundedBoxGeometry } from "three/addons/geometries/RoundedBoxGeometry.js";
import type { ActionEventName } from "./types";
import { computeOffAxisFrustum, type EyePosition } from "./viewpoint";

interface SceneOptions {
  onFrame: (now: number, deltaMs: number, aspect: number) => EyePosition;
  onFallback: (reason: string) => void;
}

const ROOM_DEPTH = 1.25;
export class HeadCoupledScene {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.PerspectiveCamera | null = null;
  private room: THREE.Group | null = null;
  private mascot: THREE.Group | null = null;
  private leftArm: THREE.Group | null = null;
  private rightArm: THREE.Group | null = null;
  private action: ActionEventName | null = null;
  private actionStartedAt = 0;
  private animationFrame = 0;
  private lastFrameAt = performance.now();
  private fpsWindowStartedAt = performance.now();
  private fpsFrames = 0;
  private renderFps = 0;
  private pixelRatio = Math.min(window.devicePixelRatio || 1, 1.5);
  private lowFpsWindows = 0;
  private enabled = true;
  private failed = false;
  private screenWidth = 1;
  private screenHeight = 1;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly options: SceneOptions,
  ) {}

  initialize(): boolean {
    try {
      this.renderer = new THREE.WebGLRenderer({
        canvas: this.canvas,
        antialias: false,
        alpha: false,
        powerPreference: "high-performance",
      });
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.setPixelRatio(this.pixelRatio);
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x294a60);
      this.camera = new THREE.PerspectiveCamera(50, 1, 0.05, 20);
      this.scene.add(new THREE.HemisphereLight(0xe7fbff, 0x203848, 2.4));
      const keyLight = new THREE.DirectionalLight(0xffffff, 3.2);
      keyLight.position.set(-1.8, 2.5, 2.8);
      this.scene.add(keyLight);
      const rimLight = new THREE.PointLight(0x61e5de, 5, 5);
      rimLight.position.set(1.2, 0.8, -0.1);
      this.scene.add(rimLight);
      this.resize();
      this.start();
      return true;
    } catch (error) {
      this.fallback(error instanceof Error ? error.message : "WebGL 初始化失败");
      return false;
    }
  }

  get fps(): number {
    return this.renderFps;
  }

  get isEnabled(): boolean {
    return this.enabled && !this.failed;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.canvas.hidden = !enabled || this.failed;
  }

  trigger(eventName: ActionEventName): void {
    this.action = eventName;
    this.actionStartedAt = performance.now();
  }

  resize(): void {
    if (!this.renderer || !this.scene) return;
    const width = Math.max(1, this.canvas.clientWidth || window.innerWidth);
    const height = Math.max(1, this.canvas.clientHeight || window.innerHeight);
    this.screenWidth = 1;
    this.screenHeight = height / width;
    this.renderer.setSize(width, height, false);
    this.rebuildRoom();
  }

  dispose(): void {
    window.cancelAnimationFrame(this.animationFrame);
    this.room?.traverse((object) => {
      if (!(object instanceof THREE.Mesh)) return;
      object.geometry.dispose();
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      materials.forEach((material) => material.dispose());
    });
    this.renderer?.dispose();
  }

  private rebuildRoom(): void {
    if (!this.scene) return;
    if (this.room) {
      this.scene.remove(this.room);
      this.room.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => material.dispose());
      });
    }

    const group = new THREE.Group();
    const wallMaterial = new THREE.MeshBasicMaterial({ color: 0x496f86, side: THREE.DoubleSide });
    const floorMaterial = new THREE.MeshBasicMaterial({ color: 0x31576e, side: THREE.DoubleSide });
    const backMaterial = new THREE.MeshBasicMaterial({ color: 0x80a9c4 });
    const wallThickness = 0.025;

    const addBox = (width: number, height: number, depth: number, x: number, y: number, z: number, material: THREE.Material) => {
      const mesh = new THREE.Mesh(new THREE.BoxGeometry(width, height, depth), material.clone());
      mesh.position.set(x, y, z);
      group.add(mesh);
    };

    addBox(wallThickness, this.screenHeight, ROOM_DEPTH, -this.screenWidth / 2, 0, -ROOM_DEPTH / 2, wallMaterial);
    addBox(wallThickness, this.screenHeight, ROOM_DEPTH, this.screenWidth / 2, 0, -ROOM_DEPTH / 2, wallMaterial);
    addBox(this.screenWidth, wallThickness, ROOM_DEPTH, 0, this.screenHeight / 2, -ROOM_DEPTH / 2, wallMaterial);
    addBox(this.screenWidth, wallThickness, ROOM_DEPTH, 0, -this.screenHeight / 2, -ROOM_DEPTH / 2, floorMaterial);
    addBox(this.screenWidth, this.screenHeight, 0.02, 0, 0, -ROOM_DEPTH - 0.015, backMaterial);

    const stage = new THREE.Mesh(
      new THREE.CylinderGeometry(0.39, 0.46, 0.1, 32),
      new THREE.MeshStandardMaterial({ color: 0x315f78, metalness: 0.45, roughness: 0.38 }),
    );
    stage.position.set(0, -0.58, -0.79);
    group.add(stage);

    const halo = new THREE.Mesh(
      new THREE.TorusGeometry(0.34, 0.012, 8, 48),
      new THREE.MeshStandardMaterial({ color: 0x77fff1, emissive: 0x1b8b88, emissiveIntensity: 2 }),
    );
    halo.rotation.x = Math.PI / 2;
    halo.position.set(0, -0.515, -0.79);
    group.add(halo);

    const mascot = this.createMascot();
    mascot.position.set(0, -0.08, -0.79);
    group.add(mascot);
    this.mascot = mascot;

    for (let index = 1; index <= 4; index += 1) {
      const z = -(ROOM_DEPTH * index) / 5;
      const lineMaterial = new THREE.MeshBasicMaterial({ color: 0x79a8b9, transparent: true, opacity: 0.45 });
      addBox(this.screenWidth, 0.008, 0.008, 0, -this.screenHeight / 2 + 0.02, z, lineMaterial);
      addBox(0.008, this.screenHeight, 0.008, -this.screenWidth / 2 + 0.02, 0, z, lineMaterial);
      addBox(0.008, this.screenHeight, 0.008, this.screenWidth / 2 - 0.02, 0, z, lineMaterial);
    }

    this.room = group;
    this.scene.add(group);
  }

  private start(): void {
    const render = (now: number) => {
      this.animationFrame = window.requestAnimationFrame(render);
      if (!this.renderer || !this.scene || !this.camera || !this.enabled || document.hidden) {
        this.lastFrameAt = now;
        return;
      }
      const deltaMs = Math.min(100, now - this.lastFrameAt);
      this.lastFrameAt = now;
      const aspect = this.screenWidth / this.screenHeight;
      const eye = this.options.onFrame(now, deltaMs, aspect);
      this.animateMascot(now);
      const frustum = computeOffAxisFrustum(eye, this.screenWidth, this.screenHeight);
      this.camera.position.set(eye.x, eye.y, eye.z);
      this.camera.rotation.set(0, 0, 0);
      this.camera.projectionMatrix.makePerspective(
        frustum.left,
        frustum.right,
        frustum.top,
        frustum.bottom,
        frustum.near,
        frustum.far,
      );
      this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();
      this.camera.updateMatrixWorld();
      this.renderer.render(this.scene, this.camera);
      this.measurePerformance(now);
    };
    this.animationFrame = window.requestAnimationFrame(render);
  }

  private createMascot(): THREE.Group {
    const robot = new THREE.Group();
    const orange = new THREE.MeshStandardMaterial({ color: 0xf2a138, metalness: 0.2, roughness: 0.45 });
    const cream = new THREE.MeshStandardMaterial({ color: 0xe9ebe4, metalness: 0.05, roughness: 0.78 });
    const peach = new THREE.MeshStandardMaterial({ color: 0xe8a18e, metalness: 0.12, roughness: 0.55 });
    const dark = new THREE.MeshStandardMaterial({ color: 0x172f3d, metalness: 0.7, roughness: 0.25 });
    const glow = new THREE.MeshStandardMaterial({
      color: 0x74fff2,
      emissive: 0x1a9992,
      emissiveIntensity: 2.5,
      metalness: 0.25,
      roughness: 0.22,
    });
    const yellow = new THREE.MeshStandardMaterial({ color: 0xe2b84b, metalness: 0.08, roughness: 0.7 });

    const body = new THREE.Mesh(new RoundedBoxGeometry(0.36, 0.38, 0.24, 4, 0.06), cream);
    body.position.y = -0.08;
    robot.add(body);

    const belt = new THREE.Mesh(new RoundedBoxGeometry(0.38, 0.07, 0.26, 3, 0.025), dark);
    belt.position.y = -0.22;
    robot.add(belt);

    const neck = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 0.09, 16), peach);
    neck.position.y = 0.16;
    robot.add(neck);

    const head = new THREE.Mesh(new RoundedBoxGeometry(0.48, 0.29, 0.31, 5, 0.065), orange);
    head.position.y = 0.34;
    robot.add(head);

    const visor = new THREE.Mesh(new RoundedBoxGeometry(0.38, 0.16, 0.035, 4, 0.035), dark);
    visor.position.set(0, 0.34, 0.17);
    robot.add(visor);

    for (const x of [-0.105, 0.105]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.047, 18, 12), glow);
      eye.position.set(x, 0.345, 0.2);
      robot.add(eye);
    }

    const antenna = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.018, 0.13, 10), dark);
    antenna.position.set(0.09, 0.55, 0);
    antenna.rotation.z = -0.18;
    robot.add(antenna);
    const antennaTip = new THREE.Mesh(new THREE.SphereGeometry(0.035, 14, 10), glow);
    antennaTip.position.set(0.102, 0.62, 0);
    robot.add(antennaTip);

    for (const x of [-0.265, 0.265]) {
      const ear = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 0.055, 20), dark);
      ear.rotation.z = Math.PI / 2;
      ear.position.set(x, 0.34, 0);
      robot.add(ear);
    }

    const createArm = (side: -1 | 1) => {
      const pivot = new THREE.Group();
      pivot.position.set(side * 0.235, 0.06, 0);
      const upper = new THREE.Mesh(new THREE.CapsuleGeometry(0.045, 0.17, 6, 12), peach);
      upper.position.y = -0.11;
      pivot.add(upper);
      const cuff = new THREE.Mesh(new THREE.CylinderGeometry(0.064, 0.058, 0.09, 16), cream);
      cuff.position.y = -0.245;
      pivot.add(cuff);
      const hand = new THREE.Mesh(new THREE.SphereGeometry(0.052, 16, 12), peach);
      hand.scale.set(0.8, 1.15, 0.72);
      hand.position.y = -0.32;
      pivot.add(hand);
      robot.add(pivot);
      return pivot;
    };
    this.leftArm = createArm(-1);
    this.rightArm = createArm(1);

    for (const side of [-1, 1]) {
      const leg = new THREE.Mesh(new THREE.CapsuleGeometry(0.06, 0.19, 6, 12), yellow);
      leg.position.set(side * 0.1, -0.4, 0);
      robot.add(leg);
      const foot = new THREE.Mesh(new RoundedBoxGeometry(0.15, 0.075, 0.22, 3, 0.025), cream);
      foot.position.set(side * 0.1, -0.54, 0.035);
      robot.add(foot);
    }
    return robot;
  }

  private animateMascot(now: number): void {
    if (!this.mascot || !this.leftArm || !this.rightArm) return;
    const idle = now / 1_000;
    this.mascot.position.y = -0.08 + Math.sin(idle * 2.1) * 0.012;
    this.mascot.rotation.x = Math.sin(idle * 1.35) * 0.015;
    this.mascot.rotation.y = Math.sin(idle * 0.72) * 0.08;
    this.leftArm.rotation.set(0, 0, -0.12);
    this.rightArm.rotation.set(0, 0, 0.12);

    if (!this.action) return;
    const elapsed = (now - this.actionStartedAt) / 1_000;
    if (elapsed > 2.4) {
      this.action = null;
      return;
    }
    const envelope = Math.sin(Math.min(1, elapsed / 2.4) * Math.PI);
    const wave = Math.sin(elapsed * 10) * 0.32;
    switch (this.action) {
      case "USER_ENTERED":
      case "USER_WAVED":
        this.rightArm.rotation.z = 2.35 + wave;
        this.rightArm.rotation.x = wave * 0.35;
        break;
      case "USER_LEFT_HAND":
        this.leftArm.rotation.z = -2.45 * envelope;
        break;
      case "USER_RIGHT_HAND":
        this.rightArm.rotation.z = 2.45 * envelope;
        break;
      case "USER_BOTH_HANDS":
        this.leftArm.rotation.z = -2.45 * envelope;
        this.rightArm.rotation.z = 2.45 * envelope;
        this.mascot.position.y += Math.abs(Math.sin(elapsed * 6)) * 0.05 * envelope;
        break;
      case "USER_ARMS_OPEN":
        this.leftArm.rotation.z = -1.45 * envelope;
        this.rightArm.rotation.z = 1.45 * envelope;
        break;
      case "USER_LEFT":
        this.mascot.rotation.y += envelope * 0.8;
        this.rightArm.rotation.z = 2.1 + wave * 0.6;
        break;
    }
  }

  private measurePerformance(now: number): void {
    this.fpsFrames += 1;
    const elapsed = now - this.fpsWindowStartedAt;
    if (elapsed < 2_000) return;
    this.renderFps = (this.fpsFrames * 1_000) / elapsed;
    this.fpsFrames = 0;
    this.fpsWindowStartedAt = now;

    if (this.renderFps < 30 && this.pixelRatio > 1) {
      this.pixelRatio = 1;
      this.renderer?.setPixelRatio(1);
      this.resize();
      this.lowFpsWindows = 0;
      return;
    }
    this.lowFpsWindows = this.renderFps < 22 ? this.lowFpsWindows + 1 : 0;
    if (this.lowFpsWindows >= 2) this.fallback("设备持续低于22fps，已切回普通视频模式");
  }

  private fallback(reason: string): void {
    if (this.failed) return;
    this.failed = true;
    this.enabled = false;
    this.canvas.hidden = true;
    this.options.onFallback(reason);
  }
}
